import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import IdentifiersSection from '../../components/identity/IdentifiersSection.jsx';
import LogoSection from '../../components/identity/LogoSection.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDateRange } from '../../utils/format.js';
import NameModal from './NameModal.jsx';

export default function Brand() {
  const { brandId } = useParams();
  const { organisation: current } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);   // 'rename' | 'archive' | 'restore'

  const base = current ? `/portal/organisations/${current.id}` : '';
  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(`${base}/brands/${brandId}`)); setError(''); } catch (err) { setError(err.status === 404 ? 'This brand doesn’t exist.' : err.message); }
  }, [base, brandId, current]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="page"><PageHeader back={{ to: '/company', label: 'Company' }} title="Brand" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  const { organisation: org, brand, identifiers, inherited, events } = data;
  const archived = brand.status === 'archived';

  const patch = async (body) => {
    await api(`${base}/brands/${brand.id}`, { method: 'PATCH', body });
    setModal(null);
    await load();
  };

  return (
    <div className="page">
      <PageHeader
        back={{ to: '/company', label: 'Company' }}
        title={brand.name}
        meta={<><span className="muted">Brand of {org.name}</span> {archived && <Pill>Archived</Pill>}</>}
        actions={org.can_manage && (
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModal('rename')}>Rename</button>
            {archived
              ? <button type="button" className="btn btn-secondary" onClick={() => setModal('restore')}>Restore</button>
              : <button type="button" className="btn btn-danger-ghost" onClick={() => setModal('archive')}>Archive</button>}
          </>
        )}
      />

      {archived && <Alert tone="warn">This brand is archived: its channels no longer count for any event.</Alert>}

      <LogoSection base={base} entityId={brand.id} ownerName={brand.name} canEdit={org.can_manage}
        description={`Shown on the public pages of ${brand.name}’s events (unless an event has its own), instead of the company logo.`} />

      <IdentifiersSection
        title="Brand channels"
        description={`Domains, websites and accounts that belong to ${brand.name}. Its events use these and the company’s.`}
        basePath={`${base}/entities/${brand.id}/identifiers`}
        ownerName={brand.name}
        initial={identifiers}
        canEdit={org.can_manage}
        inherited={inherited}
        inheritedTitle={`Also trusted: ${org.name}`}
        inheritedDescription="The company’s channels apply to every brand. Change them on the company page."
      />

      <Section title="Events" flush={events.length > 0}
        actions={<Link to="/events" className="btn btn-secondary btn-small">All events</Link>}>
        {events.length === 0 ? (
          <EmptyState>No events under this brand yet. Choose this brand when you create an event.</EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Event</th><th>Dates</th><th>Status</th></tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td data-label="Event"><Link className="strong-link" to={`/events/${e.id}`}>{e.name}</Link></td>
                    <td data-label="Dates">{fmtDateRange(e.starts_on, e.ends_on)}</td>
                    <td data-label="Status"><Pill tone={e.status === 'live' ? 'good' : 'warn'}>{e.status === 'live' ? 'Live' : 'Draft'}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {modal === 'rename' && (
        <NameModal title="Rename the brand" label="Brand name" initial={brand.name} submitLabel="Save"
          onClose={() => setModal(null)} onSave={(name) => patch({ name })} />
      )}
      {modal === 'archive' && (
        <ConfirmModal title={`Archive ${brand.name}?`} confirmLabel="Archive" danger onClose={() => setModal(null)} onConfirm={() => patch({ status: 'archived' })}>
          <p>Its channels stop counting for any event, so checks against them will say they cannot be verified. You can restore it later.</p>
        </ConfirmModal>
      )}
      {modal === 'restore' && (
        <ConfirmModal title={`Restore ${brand.name}?`} confirmLabel="Restore" onClose={() => setModal(null)} onConfirm={() => patch({ status: 'active' })}>
          <p>Its channels count again for its events.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
